import React, { useState } from 'react';
import { toast } from 'react-toastify';
import { searchPatients } from '../utils/api';
import { printRegistrationSlip } from '../utils/printSlip';

const NAVY = '#0d2137';
const GREEN = '#1a6b3c';
const RED = '#c41e3a';

export default function PatientHistory() {
  const [query, setQuery] = useState('');
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async () => {
    if (!query.trim()) return toast.warn('Please enter a name, MRN, CNIC, or phone number');
    setSearching(true);
    try {
      const r = await searchPatients(query);
      setResults(r.data);
      setSearched(true);
      if (r.data.length === 0) toast.info('No patient found');
    } catch {
      toast.error('Search failed');
    }
    setSearching(false);
  };

  return (
    <div style={{ padding: '32px 28px' }}>
      <h1 style={{ color: NAVY, fontSize: 26, fontWeight: 900, marginBottom: 4 }}>Patient History</h1>
      <div style={{ color: '#64748b', marginBottom: 24, fontSize: 14 }}>Search a patient to view their registration and visit history</div>

      <div style={{ background: '#fff', borderRadius: 10, padding: '16px 20px', marginBottom: 24, boxShadow: '0 1px 4px rgba(0,0,0,0.07)', border: '1px solid #e2e8f0' }}>
        <div style={{ display: 'flex', gap: 8 }}>
          <input
            style={{ flex: 1, padding: '10px 14px', border: '1.5px solid #cbd5e1', borderRadius: 7, fontSize: 14, outline: 'none' }}
            placeholder="Search by Name / MRN / CNIC / Phone..."
            value={query}
            onChange={e => setQuery(e.target.value)}
            onKeyDown={e => e.key === 'Enter' && handleSearch()}
            autoFocus
          />
          <button
            onClick={handleSearch}
            disabled={searching}
            style={{ padding: '10px 28px', background: NAVY, color: '#fff', border: 'none', borderRadius: 7, cursor: 'pointer', fontWeight: 700, fontSize: 14 }}>
            {searching ? '...' : 'Search'}
          </button>
        </div>
      </div>

      {searched && results.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: '#94a3b8', fontSize: 15 }}>No patients found.</div>
      )}

      {results.map(p => (
        <div key={p.id} style={{ background: '#fff', borderRadius: 12, marginBottom: 16, boxShadow: '0 2px 10px rgba(0,0,0,0.07)', overflow: 'hidden' }}>
          {/* Patient header */}
          <div style={{ background: NAVY, color: '#fff', padding: '14px 20px', display: 'flex', justifyContent: 'space-between', alignItems: 'center', flexWrap: 'wrap', gap: 8 }}>
            <div>
              <div style={{ fontWeight: 800, fontSize: 16 }}>{p.full_name}</div>
              <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 2 }}>MRN: {p.mrn} · {p.gender} · {p.age_years ? `${p.age_years} yrs` : '—'} · {p.phone || '—'}</div>
            </div>
            <div style={{ fontSize: 12, color: '#94a3b8' }}>{p.city || ''}{p.blood_group ? ` · ${p.blood_group}` : ''}</div>
          </div>

          {/* Visit history */}
          {p.visits && p.visits.length > 0 ? (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ background: '#f8fafc' }}>
                  {['Date', 'Token', 'Doctor', 'Complaint', 'Fees', 'Print'].map(h => (
                    <th key={h} style={{ padding: '9px 14px', fontSize: 12, fontWeight: 700, color: '#374151', textAlign: 'left', borderBottom: '1px solid #e2e8f0' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {p.visits.map((v, i) => (
                  <tr key={v.id} style={{ borderBottom: '1px solid #f1f5f9', background: i % 2 === 0 ? '#fff' : '#f8fafc' }}>
                    <td style={{ padding: '9px 14px', fontSize: 12, color: '#64748b' }}>
                      {v.created_at ? new Date(v.created_at).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                    </td>
                    <td style={{ padding: '9px 14px', fontWeight: 700, color: RED, fontSize: 13 }}>{v.visit_token}</td>
                    <td style={{ padding: '9px 14px', fontSize: 12, color: GREEN, fontWeight: 600 }}>{v.doctor_name || v.doctor?.name || '—'}</td>
                    <td style={{ padding: '9px 14px', fontSize: 12 }}>{v.chief_complaint || '—'}</td>
                    <td style={{ padding: '9px 14px', fontSize: 12, fontWeight: 700 }}>{v.doctor_fees || v.fees ? `PKR ${Number(v.doctor_fees || v.fees).toLocaleString()}` : '—'}</td>
                    <td style={{ padding: '9px 14px' }}>
                      <button
                        onClick={() => printRegistrationSlip(p, v.doctor_name ? { name: v.doctor_name, specialty: v.specialty, fees: v.doctor_fees || v.fees, room_number: v.room_number, timing: v.timing } : v.doctor, v.mrn || p.mrn, v.visit_token)}
                        style={{ padding: '4px 10px', background: NAVY, color: '#fff', border: 'none', borderRadius: 5, cursor: 'pointer', fontSize: 11, fontWeight: 600 }}>
                        Print
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          ) : (
            <div style={{ padding: '16px 20px', color: '#94a3b8', fontSize: 13 }}>No visits on record.</div>
          )}
        </div>
      ))}
    </div>
  );
}
